import configData from '../config.json';

const MAX_QUEUE_SIZE = 30;

let appInsights = null;
let initializationPromise = null;
const telemetryQueue = [];

function isUnresolvedToken(value) {
  return typeof value === 'string' && /#\{.+\}#/.test(value);
}

const instrumentationKey = configData.instrumentationKey;

export const isAppInsightsEnabled =
  typeof instrumentationKey === 'string' &&
  instrumentationKey.trim() !== '' &&
  !isUnresolvedToken(instrumentationKey);

function enqueueTelemetry(item) {
  if (telemetryQueue.length >= MAX_QUEUE_SIZE) {
    telemetryQueue.shift();
  }

  telemetryQueue.push(item);
}

function flushTelemetryQueue() {
  if (!appInsights) return;

  while (telemetryQueue.length > 0) {
    const item = telemetryQueue.shift();

    if (item.type === 'event') {
      appInsights.trackEvent({ name: item.name }, item.properties);
    } else if (item.type === 'pageView') {
      appInsights.trackPageView(item.pageView);
    } else if (item.type === 'exception') {
      appInsights.trackException({
        exception: item.error,
        properties: item.properties,
      });
    }
  }
}

export async function initAppInsights() {
  if (!isAppInsightsEnabled) {
    return null;
  }

  if (appInsights) {
    return appInsights;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = import('@microsoft/applicationinsights-web')
    .then((module) => {
      const ApplicationInsights =
        module.ApplicationInsights || module.default?.ApplicationInsights;

      if (!ApplicationInsights) {
        throw new Error('ApplicationInsights export was not found');
      }

      appInsights = new ApplicationInsights({
        config: {
          instrumentationKey,
          enableAutoRouteTracking: false,
          disablePageUnloadEvents: ['unload'],
        },
      });

      appInsights.loadAppInsights();
      flushTelemetryQueue();

      return appInsights;
    })
    .catch((error) => {
      initializationPromise = null;
      console.error('Failed to initialize Application Insights', error);
      return null;
    });

  return initializationPromise;
}

export function getAppInsights() {
  return appInsights;
}

export function trackAppEvent(name, properties = {}) {
  if (!isAppInsightsEnabled || !name) return;

  if (!appInsights) {
    enqueueTelemetry({ type: 'event', name, properties });
    return;
  }

  appInsights.trackEvent({ name }, properties);
}

export function trackAppPageView(pageView = {}) {
  if (!isAppInsightsEnabled) return;

  if (!appInsights) {
    enqueueTelemetry({ type: 'pageView', pageView });
    return;
  }

  appInsights.trackPageView(pageView);
}

export function trackAppException(error, properties = {}) {
  if (!isAppInsightsEnabled || !error) return;

  if (!appInsights) {
    enqueueTelemetry({
      type: 'exception',
      error,
      properties,
    });
    return;
  }

  appInsights.trackException({
    exception: error,
    properties,
  });
}
